import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import {
  CaretDownOutlined,
  CaretRightOutlined,
  CheckOutlined,
  CloseOutlined,
  CopyTwoTone,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { Button, Input, Space, Tooltip, Typography } from 'antd'
import {
  get,
  includes,
  isEmpty,
  isEqual,
  isNil,
  omit,
  pick,
  size,
} from 'lodash'
import { nanoid } from 'nanoid'
import PropTypes from 'prop-types'

import { areEqualByProps } from '../../../core/utils'

import {
  INGREDIENT_KEYWORD_COLUMN_ALLOWED_OPERATIONS,
  INGREDIENT_KEYWORD_TYPES,
  INGREDIENT_KEYWORDS_TYPE_LEVELS,
} from '../../constants'
import { getIngredientKeywordCountText } from '../../helpers'
import { idPropType } from '../../prop-types'
import { StyledAlert, StyledVerticalSpace } from '../styled'

import SearchHighlightedSpan from './search-highlighted-span'

const IngredientKeywordNodeTitle = ({
  searchValue,
  node,
  languageName,
  editable,
  onAdd,
  onRename,
  setDataForRenaming,
  isAllExpanded,
  toggleTreeExpandAll,
  showRenameInput,
  dataForRenaming,
}) => {
  const [nodeTerm, setNodeTerm] = useState(node.term)
  const [isCopied, setIsCopied] = useState(false)
  const isRootNode = useMemo(() => node.level === 1, [node])
  const countText = useMemo(() => getIngredientKeywordCountText(node), [node])

  const isSubtypeNode = useMemo(
    () => includes(INGREDIENT_KEYWORDS_TYPE_LEVELS, node.level),
    [node.level],
  )

  const isRenameButtonShown = useMemo(
    () =>
      !node.isName &&
      isEmpty(dataForRenaming) &&
      !isSubtypeNode &&
      editable &&
      !isNil(onRename) &&
      includes(
        node.allowedOperations,
        INGREDIENT_KEYWORD_COLUMN_ALLOWED_OPERATIONS.RENAME,
      ),
    [
      node.isName,
      node.allowedOperations,
      dataForRenaming,
      isSubtypeNode,
      editable,
      onRename,
    ],
  )

  const isAddChildButtonShown = useMemo(
    () =>
      isSubtypeNode &&
      editable &&
      !isNil(onAdd) &&
      includes(
        node.allowedOperations,
        INGREDIENT_KEYWORD_COLUMN_ALLOWED_OPERATIONS.ADD,
      ),
    [isSubtypeNode, editable, onAdd, node.allowedOperations],
  )

  const handleOnAddNode = useCallback(() => {
    const indexToAddNodeAt = size(node.children)

    const newNode = {
      id: nanoid(),
      term: '',
    }

    onAdd({
      ...newNode,
      children: [],
      key: `${node.key}-${indexToAddNodeAt}`,
      path: `${node.path}.children[${indexToAddNodeAt}]`,
      keyPath: [...node.keyPath, `${node.key}-${indexToAddNodeAt}`],
      parentPath: node.path,
      parentId: node.id ?? null,
      level: node.level + 1,
      allowedOperations: node.allowedOperations,
      type: node.type,
      subtype: node.subtype,
    })
  }, [onAdd, node])

  const handleConfirmRename = useCallback(() => {
    onRename({ ...pick(node, 'path'), term: nodeTerm })
    setDataForRenaming({})
  }, [node, nodeTerm, onRename, setDataForRenaming])

  const handleCancelRename = useCallback(() => {
    setNodeTerm(node.term)
    setDataForRenaming({})
  }, [node.term, setDataForRenaming])

  const handleOnRename = useCallback(event => {
    setNodeTerm(event.target.value)
  }, [])

  const handleSetIsRenamingNode = useCallback(() => {
    setDataForRenaming(showRenameInput ? {} : pick(node, ['id', 'type']))
  }, [setDataForRenaming, showRenameInput, node])

  const copyScraperQualityTrackerData = useCallback(() => {
    navigator.clipboard.writeText(nodeTerm)
    setIsCopied(true)
  }, [nodeTerm])

  const nodeContent = useMemo(() => {
    if (!showRenameInput) {
      return (
        <Space>
          <SearchHighlightedSpan searchValue={searchValue}>
            {node.term}
          </SearchHighlightedSpan>
          {!isEmpty(languageName) && (
            <Typography.Text>({languageName})</Typography.Text>
          )}
          {!isEmpty(node.suggestedType) && (
            <Typography.Text>
              ({get(INGREDIENT_KEYWORD_TYPES, `${node.suggestedType}.term`)})
            </Typography.Text>
          )}
          {!isEmpty(countText) && (
            <Typography.Text>({countText})</Typography.Text>
          )}
          {isCopied ? (
            <Tooltip title="Copied">
              <CheckOutlined style={{ color: '#52c41a' }} />
            </Tooltip>
          ) : (
            !isRootNode &&
            !isSubtypeNode && (
              <Tooltip title="Copy keyword">
                <CopyTwoTone onClick={copyScraperQualityTrackerData} />
              </Tooltip>
            )
          )}
          {isRenameButtonShown && (
            <Tooltip title="Edit Name">
              <Button
                onClick={handleSetIsRenamingNode}
                size="small"
                shape="circle"
                icon={<EditOutlined />}
              />
            </Tooltip>
          )}
          {isAddChildButtonShown && (
            <Tooltip title="Add Child Keyword">
              <Button
                size="small"
                shape="circle"
                icon={<PlusOutlined />}
                onClick={handleOnAddNode}
              />
            </Tooltip>
          )}

          {isRootNode && (
            <Tooltip title={isAllExpanded ? 'Collapse' : 'Expand'}>
              <Button
                size="small"
                shape="circle"
                icon={
                  isAllExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />
                }
                onClick={toggleTreeExpandAll}
              />
            </Tooltip>
          )}
        </Space>
      )
    }

    return (
      <Space>
        <StyledVerticalSpace>
          <Input
            style={
              isEmpty(nodeTerm) ? { border: '1px solid #ff4d4f' } : undefined
            }
            value={nodeTerm}
            placeholder="Input term..."
            onChange={handleOnRename}
          />
          {isEmpty(nodeTerm) && (
            <Typography.Text type="danger">Please input Term!</Typography.Text>
          )}
        </StyledVerticalSpace>
        <Button
          size="small"
          shape="circle"
          icon={<CheckOutlined />}
          onClick={handleConfirmRename}
        />
        <Button
          size="small"
          shape="circle"
          icon={<CloseOutlined />}
          onClick={handleCancelRename}
        />
      </Space>
    )
  }, [
    copyScraperQualityTrackerData,
    countText,
    handleCancelRename,
    handleConfirmRename,
    handleOnAddNode,
    handleOnRename,
    handleSetIsRenamingNode,
    isAddChildButtonShown,
    isAllExpanded,
    isCopied,
    isRenameButtonShown,
    isRootNode,
    isSubtypeNode,
    languageName,
    node.suggestedType,
    node.term,
    nodeTerm,
    searchValue,
    showRenameInput,
    toggleTreeExpandAll,
  ])

  useEffect(() => {
    if (!isEmpty(node.term)) {
      setNodeTerm(node.term)
    }
  }, [node.term])

  useEffect(() => {
    let copiedTimeout

    if (isCopied) {
      copiedTimeout = setTimeout(() => setIsCopied(false), 2000)
    }

    return () => clearTimeout(copiedTimeout)
  }, [isCopied])

  if (!node.isModified) {
    return nodeContent
  }

  return (
    <StyledAlert banner showIcon={false} message={nodeContent} type="info" />
  )
}

IngredientKeywordNodeTitle.propTypes = {
  node: PropTypes.object.isRequired,
  searchValue: PropTypes.string,
  languageName: PropTypes.string,
  editable: PropTypes.bool,
  onAdd: PropTypes.func,
  onRename: PropTypes.func,
  setDataForRenaming: PropTypes.func,
  toggleTreeExpandAll: PropTypes.func,
  isAllExpanded: PropTypes.bool,
  showRenameInput: PropTypes.bool,
  dataForRenaming: PropTypes.shape({ id: idPropType }),
}

IngredientKeywordNodeTitle.defaultProps = {
  searchValue: '',
  languageName: '',
  editable: false,
  onAdd: undefined,
  onRename: undefined,
  setDataForRenaming: undefined,
  toggleTreeExpandAll: undefined,
  showRenameInput: false,
  isAllExpanded: false,
  dataForRenaming: {},
}

export default memo(
  IngredientKeywordNodeTitle,
  ({ node: prevNode, ...restPrevious }, { node: nextNode, ...restNext }) =>
    isEqual(omit(prevNode, 'children'), omit(nextNode, 'children')) &&
    size(prevNode.children) === size(nextNode.children) &&
    areEqualByProps(restPrevious, restNext, [
      'searchValue',
      'languageName',
      'isDirty',
      'shouldEdit',
      'editable',
      'isAllExpanded',
      'dataForRenaming.id',
    ]),
)
